import React, { VFC } from 'react';
import { css, keyframes } from '@emotion/css';
import tencentLogo from '../../assets/images/p4/Tencent-Logo.png';
import nvidiaLogo from '../../assets/images/p4/NVIDIA_logo.png';
import minimaxLogo from '../../assets/images/p4/minimax.png';
import microsoftLogo from '../../assets/images/p4/Microsoft-Logo.png';
import googleLogo from '../../assets/images/p4/google.png';
import baiduLogo from '../../assets/images/p4/baiduqianfaa.png';
import aliyunLogo from '../../assets/images/p4/aliyun.png';
import daojialeLogo from '../../assets/images/p4/daojiale.png';
import deepseekLogo from '../../assets/images/p4/deepseek_logo.png';
import fastgptLogo from '../../assets/images/p4/fastgpt.png';
import moonshotLogo from '../../assets/images/p4/moonshot.png';
import zhipoLogo from '../../assets/images/p4/zhipo.png';

const partnerLogos = [
  { src: tencentLogo, alt: 'Tencent 腾讯' },
  { src: nvidiaLogo, alt: 'NVIDIA' },
  { src: minimaxLogo, alt: 'MiniMax' },
  { src: microsoftLogo, alt: 'Microsoft' },
  { src: googleLogo, alt: 'Google' },
  { src: baiduLogo, alt: '百度千帆' },
  { src: aliyunLogo, alt: '阿里云' },
  { src: daojialeLogo, alt: '到家了' },
  { src: deepseekLogo, alt: 'Deepseek' },
  { src: fastgptLogo, alt: 'FastGPT' },
  { src: moonshotLogo, alt: '月之暗面 Moonshot' },
  { src: zhipoLogo, alt: '智谱 AI' },
];

const topRow = partnerLogos.slice(0, 6);
const bottomRow = partnerLogos.slice(6);

// 复制slides以确保循环滚动流畅 (复制2次足够)
const topRowRepeated = [...topRow, ...topRow];
const bottomRowRepeated = [...bottomRow, ...bottomRow];

export const P4Section: VFC = () => {
  return (
    <section className={styles.wrapper} aria-label="P4">
      <h2 className={styles.title}>技术生态与合作伙伴</h2>
      <p className={styles.desc}>
        我们与行业领导者紧密合作，构建稳定、先进、可信赖的 AI 智能体解决方案。
      </p>
      <div className={styles.scrollContainer}>
        <div className={styles.rowTop}>
          <div className={styles.scrollTrack}>
            {topRowRepeated.map((logo, i) => (
              <div key={i} className={styles.slide}>
                <div className={styles.partnerCard}>
                  <img src={logo.src} alt={logo.alt} className={styles.partnerLogo} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.rowBottom}>
          <div className={styles.scrollTrack}>
            {bottomRowRepeated.map((logo, i) => (
              <div key={i} className={styles.slide}>
                <div className={styles.partnerCard}>
                  <img src={logo.src} alt={logo.alt} className={styles.partnerLogo} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// CSS动画：无缝循环滚动
const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const styles = {
  wrapper: css`
    pointer-events: none;
    position: relative;
    min-height: calc(var(--vh, 1vh) * 100);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 6vh 0;
    width: min(1400px, 92vw);
    color: #fff;
  `,
  title: css`
    pointer-events: auto;
    text-align: center;
    font-size: 42px;
    font-weight: 800;
    margin: 0 0 5px 0;
    opacity: 0;
    transform: translate3d(30px, 0, 0);
    transition: opacity 0.7s ease, transform 0.7s ease;
    .reveal-visible & {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
    @media (max-width: 768px) {
      font-size: 32px;
    }
  `,
  desc: css`
    pointer-events: auto;
    text-align: center;
    margin: 0 auto 60px;
    max-width: 760px;
    font-size: 20px;
    opacity: 0;
    transform: translate3d(-30px, 0, 0);
    transition: opacity 0.7s ease, transform 0.7s ease;
    .reveal-visible & {
      opacity: 0.9;
      transform: translate3d(0, 0, 0);
    }
    @media (max-width: 768px) {
      font-size: 15px;
      margin-bottom: 30px;
    }
  `,
  scrollContainer: css`
    pointer-events: auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    opacity: 0;
    transition: opacity 0.7s ease 0.1s;

    .reveal-visible & {
      opacity: 1;
    }
  `,
  rowTop: css`
    width: 100%;
    overflow: hidden;
  `,
  rowBottom: css`
    width: 100%;
    overflow: hidden;
  `,
  scrollTrack: css`
    display: flex;
    gap: 16px;
    animation: ${scrollAnimation} 30s linear infinite;
    width: fit-content;
    cursor: pointer;
    &:hover {
      animation-play-state: paused;
    }
  `,
  slide: css`
    width: 250px;
    height: 130px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 768px) {
      width: 160px;
      height: 80px;
    }
  `,
  partnerCard: css`
    width: 250px;
    height: 130px;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    
    @media (max-width: 768px) {
      width: 160px;
      height: 80px;
    }
  `,
  partnerLogo: css`
    max-width: 90%;
    max-height: 70%;
    object-fit: contain;
  `,
};

